import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import {
  useCreateOrganizer,
  useUpdateOrganizer,
  type Organizer,
} from "@/hooks/useOrganizers";
import { useToast } from "@/hooks/useToast";
import { ORGANIZER_CATEGORIES } from "@/utils/constants";
import type { OrganizerCategory } from "@/types/database";

interface OrganizerFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (organizerId: string) => void;
  organizer?: Organizer;
}

const initialFormData = {
  name: "",
  category: "Parish" as OrganizerCategory,
  email: "",
  phone: "",
  address: "",
  contact_person: "",
  notes: "",
};

export function OrganizerForm({
  open,
  onClose,
  onSuccess,
  organizer,
}: OrganizerFormProps) {
  const createOrganizer = useCreateOrganizer();
  const updateOrganizer = useUpdateOrganizer();
  const { toast } = useToast();

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (open) {
      setFormData(
        organizer
          ? {
              name: organizer.name,
              category: organizer.category,
              email: organizer.email || "",
              phone: organizer.phone || "",
              address: organizer.address || "",
              contact_person: organizer.contact_person || "",
              notes: organizer.notes || "",
            }
          : initialFormData,
      );
    }
  }, [open, organizer]);

  const isEditing = !!organizer;
  const isPending = createOrganizer.isPending || updateOrganizer.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await updateOrganizer.mutateAsync({
          id: organizer.id,
          updates: formData,
        });
        toast({ title: "Organisateur mis à jour" });
        onClose();
        onSuccess?.(organizer.id);
      } else {
        const newOrg = await createOrganizer.mutateAsync(formData);
        toast({ title: "Organisateur créé" });
        setFormData(initialFormData);
        onClose();
        onSuccess?.(newOrg.id);
      }
    } catch {
      toast({
        title: "Erreur",
        description: isEditing
          ? "Impossible de mettre à jour l'organisateur"
          : "Impossible de créer l'organisateur",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Modifier l'organisateur" : "Nouvel organisateur"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="org-name">Nom *</Label>
              <Input
                id="org-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label>Catégorie *</Label>
                <HelpTooltip content="Diocese : structure diocésaine. Parish : paroisse locale. Association : association loi 1901. Festival : organisateur de festival. Institution : école, hôpital, maison de retraite..." />
              </div>
              <Select
                value={formData.category}
                onValueChange={(v) =>
                  setFormData({
                    ...formData,
                    category: v as OrganizerCategory,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORGANIZER_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="org-contact_person">Personne de contact</Label>
                <HelpTooltip content="Personne principale à contacter pour cet organisateur." />
              </div>
              <Input
                id="org-contact_person"
                value={formData.contact_person}
                onChange={(e) =>
                  setFormData({ ...formData, contact_person: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="org-email">Email</Label>
              <Input
                id="org-email"
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="org-phone">Téléphone</Label>
              <Input
                id="org-phone"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
              />
            </div>

            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="org-address">Adresse</Label>
              <Input
                id="org-address"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
              />
            </div>

            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor="org-notes">Notes</Label>
              <Textarea
                id="org-notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? isEditing
                  ? "Mise à jour..."
                  : "Création..."
                : isEditing
                  ? "Mettre à jour"
                  : "Créer"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
